import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const VALID_THEMES: readonly Theme[] = ['light', 'dark', 'system'] as const;

export function isValidTheme(value: string): value is Theme {
  return VALID_THEMES.includes(value as Theme);
}

interface ThemeCtx {
  theme: Theme;
  setTheme: (t: Theme) => void;
  isDark: boolean;
}

const Ctx = createContext<ThemeCtx>({ theme: 'system', setTheme: () => {}, isDark: false });

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem('theme');
      return stored && isValidTheme(stored) ? stored : 'light';
    } catch (e) {
      console.warn('localStorage unavailable for theme preference, using default:', e);
      return 'light';
    }
  });

  const apply = (t: Theme) => {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const dark = t === 'dark' || (t === 'system' && prefersDark);
    // Add/remove .dark class on html element for CSS dark mode styles
    if (dark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const setTheme = (t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem('theme', t);
    } catch (e) {
      console.warn('Unable to save theme preference to localStorage:', e);
    }
    apply(t);
  };

  useEffect(() => {
    apply(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prefersDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);

  return <Ctx.Provider value={{ theme, setTheme, isDark }}>{children}</Ctx.Provider>;
};

export const useTheme = () => useContext(Ctx);
